import React, { FC } from 'react';
import { Box, useBreakpointValue, useTheme } from '@chakra-ui/react';
import { motion } from 'motion/react';
import Button from 'ts/common/components/gallery/Button';
import CoverPhoto from 'ts/common/components/gallery/CoverPhoto';
import GalleryHeading from 'ts/common/components/gallery/GalleryHeading';
import StudioHeading from 'ts/common/components/gallery/StudioHeading';
import StudioLogo from 'ts/common/components/gallery/StudioLogo';
import { ILayoutProps } from './BaseLayout';
import { block2RightAnimations, getAnimationProps } from './animations';

const Block2RightLayout: FC<ILayoutProps> = ({
    galleryTitle,
    brandName,
    coverPhoto,
    coverPhotoUrl,
    eventBrandLogo,
    logoAltText,
    buttonText,
    buttonTextLarge,
    colorScheme,
    onClick,
    enableAnimations = true
}) => {
    const theme = useTheme();
    const galleryHeadingSize =
        useBreakpointValue({ base: 'sm' as const, lg: 'md' as const }) ?? 'sm';
    const studioLogoSize = useBreakpointValue({ base: 'md' as const, lg: 'lg' as const });
    const brandLogoTop = useBreakpointValue({ base: '30px', lg: '15%' }) || '30px';
    const brandLogoRight = useBreakpointValue({ base: 4, lg: 8 }) || 4;

    return (
        <Box position="absolute" width="full" height="full" onClick={onClick}>
            <Box
                position="relative"
                width="full"
                height="full"
                overflow="hidden"
                background={colorScheme === 'dark' ? theme.colors.gray[700] : theme.colors.white}
                display="flex"
                flexDirection={useBreakpointValue({ base: 'row', lg: 'row' }) || 'row'}
            >
                {/* Unified Content Panel */}
                <Box
                    width={useBreakpointValue({ base: '50%', lg: '51.5%' }) || '50%'}
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    alignItems={
                        useBreakpointValue({ base: 'flex-end', lg: 'flex-end' }) || 'flex-end'
                    }
                    paddingLeft={useBreakpointValue({ base: 4, lg: 8 }) || 4}
                    paddingRight={useBreakpointValue({ base: 4, lg: 8 }) || 4}
                    paddingBottom={useBreakpointValue({ base: '80px', lg: '100px' }) || '80px'}
                    textAlign="right"
                    position="relative"
                >
                    {/* Content Container */}
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={useBreakpointValue({ base: 1, lg: 2 }) || 1}
                        width="full"
                        alignItems="flex-end"
                        marginBottom={useBreakpointValue({ base: 10, lg: 0 }) || 10}
                    >
                        <motion.div
                            {...getAnimationProps(
                                block2RightAnimations.galleryName,
                                enableAnimations
                            )}
                        >
                            <GalleryHeading
                                size={
                                    useBreakpointValue({ base: 'md', lg: galleryHeadingSize }) ??
                                    'md'
                                }
                                text={galleryTitle}
                                variant="primary"
                            />
                        </motion.div>
                        {!eventBrandLogo && (
                            <motion.div
                                {...getAnimationProps(
                                    block2RightAnimations.studioName,
                                    enableAnimations
                                )}
                            >
                                <StudioHeading text={brandName} variant="primary" />
                            </motion.div>
                        )}
                    </Box>
                    <motion.div
                        {...getAnimationProps(block2RightAnimations.viewBtn, enableAnimations)}
                    >
                        <Button
                            text={buttonText}
                            textLarge={buttonTextLarge}
                            variant="outline primary"
                            fullWidth={useBreakpointValue({ base: true, lg: false }) ?? true}
                        />
                    </motion.div>

                    {/* Brand Logo */}
                    {eventBrandLogo && (
                        <Box
                            position="absolute"
                            top={brandLogoTop}
                            right={brandLogoRight}
                            zIndex={10}
                        >
                            <motion.div
                                {...getAnimationProps(
                                    block2RightAnimations.studioName,
                                    enableAnimations
                                )}
                            >
                                <StudioLogo
                                    logo={eventBrandLogo}
                                    logoAltText={logoAltText}
                                    logoSize={studioLogoSize}
                                />
                            </motion.div>
                        </Box>
                    )}
                </Box>

                {/* Unified Cover Image */}
                <motion.div
                    style={{
                        width: useBreakpointValue({ base: '50%', lg: '48.5%' }) || '50%',
                        height: '100%',
                        overflow: 'hidden',
                        flexShrink: 0
                    }}
                    {...getAnimationProps(block2RightAnimations.coverImage, enableAnimations)}
                >
                    <CoverPhoto coverPhoto={coverPhoto} coverPhotoUrl={coverPhotoUrl} />
                </motion.div>
            </Box>
        </Box>
    );
};

export default Block2RightLayout;
